// Platform-detection helper for GlobalEyes.TV.
// -----------------------------------------------------------------------
// Single source of truth for "are we running in the Capacitor-wrapped
// native iOS/Android binary?" — used by:
//   • MarketplacePage.jsx  (hard gate: web visitors see install-prompt)
//   • Native-only feature toggles (push enrollment, in-app diagnostics)
//
// Capacitor's `isNativePlatform()` returns `false` in the regular web
// build (no Capacitor runtime injected), and `true` inside the iOS or
// Android shell. We import it dynamically so the web bundle stays
// slim — webpack tree-shakes the @capacitor/core import when this
// helper is reached only at runtime.
//
// If you need to test the gate locally without building a binary,
// append `?force_native=1` to any URL during development. This
// flag is gated to non-production NODE_ENV so it can't be abused in
// the live web bundle.

import { Capacitor } from "@capacitor/core";

let _devForceNativeCache = null;

const devForceNative = () => {
  if (_devForceNativeCache !== null) return _devForceNativeCache;
  if (process.env.NODE_ENV === "production") {
    _devForceNativeCache = false;
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    _devForceNativeCache = params.get("force_native") === "1";
  } catch (_) {
    _devForceNativeCache = false;
  }
  return _devForceNativeCache;
};

export const isNative = () => {
  try {
    return Capacitor.isNativePlatform() || devForceNative();
  } catch (_) {
    return devForceNative();
  }
};

export const platform = () => {
  try {
    return Capacitor.getPlatform();  // 'ios' | 'android' | 'web'
  } catch (_) {
    return "web";
  }
};

// Convenience constants for components that only render on web (the
// install-prompt landing) or only on native (the actual marketplace UI).
export const IS_NATIVE = isNative();
export const IS_WEB = !IS_NATIVE;
